import {Dropdown, DropdownItem, DropdownSearchItem, Tooltip} from 'platform/components';
import {Center, Hide, Link, Show} from 'platform/foundation';

import {useState} from 'react';

import {filter, includes, toUpper} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {BranchResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {NoPermissionTooltip} from '../../NoPermissionTooltip/NoPermissionTooltip';

export interface BranchChangerProps extends TestIdProps {
  recordId: string;
  onChange?: (branchId: string) => Promise<unknown> | void;
  activeBranch?: BranchResponseBody;
  isLoading?: boolean;
  isEditDisabled?: boolean;
  hideTooltip?: boolean;
  tooltip?: string;
}

export function BranchChanger(props: BranchChangerProps) {
  const {data: currentUserInfo} = useGetCurrentUserInfoQuery();
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  const branches = !searchQuery
    ? currentUserInfo?.branches
    : filter(
        (branch) => includes(toUpper(searchQuery), toUpper(branch.name)),
        currentUserInfo?.branches ?? []
      );

  const shouldShowNoPermissionTooltip = Boolean(props.isEditDisabled) && !props.hideTooltip;
  // Two tooltips on one element would stack, and the missing permission explains
  // why the control does nothing, so it wins over the control's own name.
  const tooltipLabel = shouldShowNoPermissionTooltip ? undefined : props.tooltip;

  return (
    <NoPermissionTooltip shouldShowTooltip={shouldShowNoPermissionTooltip}>
      <Tooltip
        label={tooltipLabel}
        isDisabled={isNilOrEmpty(tooltipLabel)}
        childrenWrapperDisplay="flex"
      >
        <Show when={props.isEditDisabled}>
          <Link
            size="small"
            leftIcon="maps/place"
            title={props.activeBranch?.name ?? i18n.t('entity.branch.labels.selectedBranch')}
            isDisabled
            data-testid={suffixTestId('branch', props)}
          />
        </Show>
        <Hide when={props.isEditDisabled}>
          <Dropdown
            hasSearch
            dropdownControl={
              <Center>
                <Link
                  size="small"
                  leftIcon="maps/place"
                  title={props.activeBranch?.name ?? i18n.t('entity.branch.labels.selectedBranch')}
                  isDisabled={props.isLoading || props.isEditDisabled}
                  data-testid={suffixTestId('branch', props)}
                />
              </Center>
            }
            isDisabled={props.isEditDisabled}
            onClose={() => setSearchQuery(null)}
            data-testid={suffixTestId('branch', props)}
          >
            <DropdownSearchItem value={searchQuery} onChange={setSearchQuery} />
            {branches?.map((branch) => (
              <DropdownItem
                key={branch.id}
                label={branch.name}
                onClick={() => props.onChange?.(branch.id)}
                data-testid={suffixTestId(`branch-${branch.id}`, props)}
              />
            ))}
          </Dropdown>
        </Hide>
      </Tooltip>
    </NoPermissionTooltip>
  );
}
